// src/components/SubscribeSheet.tsx
//
// App-parity subscribe bottom sheet (mirrors lib/screens/wallet/widgets/
// subscribe_sheet.dart). Self-contained: fetches the current offer via
// get_subscription_offer and runs the SAME purchase flow as MembershipCard
// (create-membership-order → Razorpay → confirm-membership). Opened from the
// wallet membership teaser and the monthly-reports "free with Aura Plus"
// banner. No backend changes.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { usePricing } from "../context/PricingContext";
import { amplitudeApp } from "../index";
import { supabase } from "../supabaseClient";
import { AnalyticsManager, trackEvent } from "../utils/analytics";
import { startPayuCheckout } from "../utils/payuBolt";
import { IconCrown, IconX } from "@tabler/icons-react";
import styles from "./SubscribeSheet.module.css";

// membership_exp_v1 arm (Amplitude), mirrors MembershipCard: onetime = pay-once,
// autopay = recurring auto-renew (PayU SI primary / Razorpay Subscription
// fallback), hidden = holdout. Unknown/hidden falls back to the one-time flow.
type MembershipArm = "hidden" | "autopay" | "onetime";
const MEMBERSHIP_ARMS: MembershipArm[] = ["hidden", "autopay", "onetime"];

interface Benefit {
  service_key: string;
  base_minor: number;
  member_minor: number;
  pct_off: number;
}
interface Offer {
  plan_id: string;
  name: string;
  interval: string;
  price_minor: number;
  currency: string;
  max_pct_off: number;
  benefits: Benefit[];
}

const SERVICE_LABELS: Record<string, string> = {
  chat_message: "Chat questions",
  voice_call_minute: "Voice calls",
  report_premium: "Premium reports",
  report_monthly: "Monthly reports",
  monthly_report: "Monthly reports",
};
const SERVICE_ORDER: Record<string, number> = {
  chat_message: 0,
  voice_call_minute: 1,
  report_premium: 2,
  report_monthly: 3,
  monthly_report: 3,
};

// Headline "save up to X%": largest partial discount (ignore 100%-off freebies).
function headlinePct(offer: Offer): number {
  const partials = offer.benefits
    .map((b) => b.pct_off)
    .filter((p) => p > 0 && p < 100);
  if (partials.length === 0) return offer.max_pct_off >= 100 ? 50 : offer.max_pct_off;
  return Math.max(...partials);
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  source?: string;
  // When true, render the offer content inline (no dimmed overlay / slide-up
  // sheet / close button) — used as the non-member "Plan" tab body.
  inline?: boolean;
}

export default function SubscribeSheet({ isOpen, onClose, source, inline = false }: Props) {
  const { user, currency, pricingVariant, refreshUserStatus } = useAuth();
  const navigate = useNavigate();
  const { formatPrice } = usePricing();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  // Default to the one-time flow — a safe fallback if arm resolution fails.
  const [arm, setArm] = useState<MembershipArm>("onetime");

  // Resolve the membership_exp_v1 arm the app-way (sticky users.membership_variant
  // wins; else Amplitude). Determines whether "Get membership" sets up a
  // recurring auto-renew (autopay) or a single charge (onetime).
  const resolveArm = useCallback(async (): Promise<MembershipArm> => {
    if (!user) return "onetime";
    try {
      const { data: row } = await supabase
        .from("users")
        .select("membership_variant")
        .eq("id", user.id)
        .maybeSingle();
      const sticky = row?.membership_variant as MembershipArm | undefined;
      if (sticky && MEMBERSHIP_ARMS.includes(sticky)) return sticky;
    } catch (e) {
      console.error("[subscribe] sticky arm read failed:", e);
    }
    try {
      await amplitudeApp.experiment.fetch();
      const raw = amplitudeApp.experiment.variant("membership_exp_v1")?.value;
      if ((MEMBERSHIP_ARMS as string[]).includes(raw || "")) return raw as MembershipArm;
    } catch (e) {
      console.error("[subscribe] arm fetch failed:", e);
    }
    return "onetime";
  }, [user]);

  const loadOffer = useCallback(async () => {
    if (!currency) return;
    setLoading(true);
    try {
      const [{ data }, resolvedArm] = await Promise.all([
        supabase.rpc("get_subscription_offer", {
          p_currency: currency,
          p_variant: pricingVariant || "control",
        }),
        resolveArm(),
      ]);
      setOffer(data && typeof data === "object" ? (data as Offer) : null);
      setArm(resolvedArm);
    } catch (e) {
      console.error("[subscribe] offer fetch failed:", e);
      setOffer(null);
    } finally {
      setLoading(false);
    }
  }, [currency, pricingVariant, resolveArm]);

  useEffect(() => {
    if (isOpen || inline) loadOffer();
  }, [isOpen, inline, loadOffer]);

  const openRazorpay = (options: any) => {
    if (!(window as any).Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => new (window as any).Razorpay(options).open();
      document.body.appendChild(script);
    } else {
      new (window as any).Razorpay(options).open();
    }
  };

  // PayU (India) hosted checkout for the Aura Plus membership — website-only,
  // PRIMARY gateway. Server resolves the price + guards double-buy; payu-callback
  // verifies + activates the membership (mirroring confirm-membership) and
  // redirects to /payment-success -> /chat. Throws so the caller can fall back to
  // Razorpay if PayU can't start (errors / admin toggle 'skip' / no PayU price).
  // An already-member throw carries `.already` so we don't fall back.
  const startPayuMembership = async (
    purpose: "membership" | "membership_autopay" = "membership",
  ): Promise<void> => {
    if (!offer) return;
    const { data, error } = await supabase.functions.invoke("create-payu-payment", {
      body: {
        purpose,
        plan_id: offer.plan_id,
        return_origin: window.location.origin,
      },
    });
    if (error) throw new Error(error.message || "payu_init_failed");
    if ((data as any)?.error === "already_subscribed") {
      const err: any = new Error("already_subscribed");
      err.already = true;
      throw err;
    }
    if (!data || (data as any).error || (data as any).skip) {
      throw new Error((data as any)?.reason || (data as any)?.error || "payu_skip");
    }
    if (!(data as any).payu_url || !(data as any).params) throw new Error("payu_bad_response");

    // Close the subscribe sheet so PayU's in-page popup isn't hidden behind it.
    onClose();

    // In-page PayU popup (Bolt) with automatic redirect fallback if it can't
    // render — so it never hangs. Settlement stays server-side (webhook / surl).
    await startPayuCheckout(data as any, (outcome) => {
      if (outcome === "success") {
        // Same purchase pixels the Razorpay membership handler fires (Meta
        // Purchase, CleverTap Charged, Amplitude) — PayU settles server-side,
        // so this popup callback is the only client moment that knows the txn.
        AnalyticsManager.logPurchase(
          String((data as any).params?.txnid || ""),
          offer.price_minor,
          offer.currency,
          [
            {
              itemId: "aura_plus_membership",
              itemName: offer.name,
              price: offer.price_minor,
              quantity: 1,
            },
          ],
        );
        window.dispatchEvent(new CustomEvent("aura:payment-success"));
        navigate("/payment-success?type=membership");
      } else {
        setPurchasing(false);
      }
    });
  };

  // Razorpay one-time membership order — the FALLBACK path (unchanged app flow).
  const startRazorpayMembership = async (): Promise<void> => {
    if (!offer) return;
    const { data, error } = await supabase.functions.invoke(
      "create-membership-order",
      { body: { plan_id: offer.plan_id } },
    );
    if (error) throw new Error(error.message || "Could not start checkout");
    if ((data as any)?.error === "already_subscribed") {
      toast("You're already a member.", { icon: "✅" });
      onClose();
      return;
    }
    if (!data?.order) throw new Error("Could not start checkout");
    const order = data.order;
    openRazorpay({
      key: data.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.id,
      name: `Aura AI ${offer.name}`,
      description: `${offer.name} membership`,
      prefill: { email: user?.email },
      theme: { color: "#FF2E93" },
      handler: async () => {
        try {
          const { data: confirm } = await supabase.functions.invoke(
            "confirm-membership",
            { body: { order_id: order.id } },
          );
          AnalyticsManager.logPurchase(order.id, order.amount, order.currency, [
            {
              itemId: "aura_plus_membership",
              itemName: offer.name,
              price: order.amount,
              quantity: 1,
            },
          ]);
          window.dispatchEvent(new CustomEvent("aura:payment-success"));
          if ((confirm as any)?.activated === true) {
            await refreshUserStatus();
            toast.success(`${offer.name} is now active!`);
          } else {
            toast("Payment received, activating your membership…", {
              icon: "⏳",
            });
          }
        } catch (e) {
          console.error("confirm-membership failed:", e);
          toast("Payment received — activation is finishing up.", { icon: "⏳" });
        } finally {
          onClose();
          navigate("/chat"); // land on chat, same as the PayU path
        }
      },
      modal: { ondismiss: () => setPurchasing(false) },
    });
  };

  // AUTOPAY (recurring) membership — Razorpay Subscription (UPI-AutoPay / card
  // mandate). This is the FALLBACK for the autopay arm; PayU SI is the intended
  // primary (Phase B). create-subscription-session makes the sub, checkout runs
  // in subscription mode, confirm-subscription activates instantly, and the
  // shared razorpay-webhook renews/cancels it. cancel_at_period_end=false.
  const startAutopaySubscription = async (): Promise<void> => {
    if (!offer) return;
    const { data, error } = await supabase.functions.invoke(
      "create-subscription-session",
      { body: { plan_id: offer.plan_id } },
    );
    if (error) throw new Error(error.message || "Could not start subscription");
    if ((data as any)?.error === "already_subscribed") {
      toast("You're already a member.", { icon: "✅" });
      onClose();
      return;
    }
    const subId = (data as any)?.subscription_id;
    if (!subId) throw new Error("Could not start subscription");
    openRazorpay({
      key: (data as any).key_id,
      subscription_id: subId, // subscription mode (not order_id)
      name: `Aura AI ${offer.name}`,
      description: `${offer.name} membership · auto-renews monthly`,
      prefill: { email: user?.email },
      theme: { color: "#FF2E93" },
      handler: async (resp: any) => {
        try {
          const { data: confirm } = await supabase.functions.invoke(
            "confirm-subscription",
            { body: { subscription_id: subId, payment_id: resp?.razorpay_payment_id } },
          );
          AnalyticsManager.logPurchase(subId, offer.price_minor, offer.currency, [
            {
              itemId: "aura_plus_membership_autopay",
              itemName: offer.name,
              price: offer.price_minor,
              quantity: 1,
            },
          ]);
          window.dispatchEvent(new CustomEvent("aura:payment-success"));
          if ((confirm as any)?.activated === true) {
            await refreshUserStatus();
            toast.success(`${offer.name} is now active!`);
          } else {
            toast("Payment received, activating your membership…", { icon: "⏳" });
          }
        } catch (e) {
          console.error("confirm-subscription failed:", e);
          toast("Payment received — activation is finishing up.", { icon: "⏳" });
        } finally {
          onClose();
          navigate("/chat");
        }
      },
      modal: { ondismiss: () => setPurchasing(false) },
    });
  };

  // Entry point. Autopay arm -> recurring subscription. Onetime arm -> PayU-first
  // single charge, Razorpay one-time fallback.
  const handleSubscribe = async () => {
    if (!offer || purchasing) return;
    setPurchasing(true);
    const isAutopay = arm === "autopay";
    trackEvent("Subscription Clicked", {
      plan_id: offer.plan_id,
      billing: isAutopay ? "autopay" : "one_time",
      source: source || "subscribe_sheet",
    });
    try {
      if (isAutopay) {
        // PayU SI (Pay-and-Subscribe mandate) first, Razorpay Subscription fallback.
        try {
          await startPayuMembership("membership_autopay");
          return; // PayU redirect in progress — page is navigating away
        } catch (e: any) {
          if (e?.already) {
            toast("You're already a member.", { icon: "✅" });
            onClose();
            return;
          }
          console.warn("[subscribe] PayU autopay unavailable, falling back to Razorpay subscription:", e);
        }
        await startAutopaySubscription();
        return;
      }
      try {
        await startPayuMembership();
        return; // PayU redirect in progress — page is navigating away
      } catch (e: any) {
        if (e?.already) {
          toast("You're already a member.", { icon: "✅" });
          onClose();
          return;
        }
        console.warn("[subscribe] PayU unavailable, falling back to Razorpay:", e);
      }
      await startRazorpayMembership();
    } catch (e: any) {
      toast.error(e.message || "Could not start checkout");
    } finally {
      // PayU navigates away so this is moot there; for Razorpay the modal's
      // ondismiss also clears it. Keep the button usable if we didn't redirect.
      setPurchasing(false);
    }
  };

  if (!inline && !isOpen) return null;

  const pct = offer ? headlinePct(offer) : 0;
  const per = offer?.interval === "year" ? "year" : "month";
  const priceStr = offer ? formatPrice(offer.price_minor) : "";
  const orderedBenefits = offer
    ? [...offer.benefits].sort(
        (a, b) =>
          (SERVICE_ORDER[a.service_key] ?? 99) -
          (SERVICE_ORDER[b.service_key] ?? 99),
      )
    : [];

  const body = (
      <>
        {loading ? (
          <div className={styles.loading}>Loading membership…</div>
        ) : !offer ? (
          <div className={styles.loading}>
            Membership isn't available right now.
          </div>
        ) : (
          <>
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.iconBadge}>
                <IconCrown size={22} />
              </div>
              <div className={styles.headerText}>
                <span className={styles.planName}>{offer.name}</span>
                <span className={styles.pricing}>
                  {priceStr} for 1 {per} · renew anytime
                </span>
              </div>
              {pct > 0 && <span className={styles.saveBadge}>SAVE {pct}%</span>}
            </div>

            {/* Benefits */}
            <p className={styles.benefitsLabel}>YOUR MEMBER PRICING</p>
            <div className={styles.benefits}>
              {orderedBenefits.map((b) => {
                const label = SERVICE_LABELS[b.service_key] || b.service_key;
                const isFree = b.pct_off >= 100;
                return (
                  <div key={b.service_key} className={styles.benefitRow}>
                    <span className={styles.benefitDot} />
                    <span className={styles.benefitLabel}>{label}</span>
                    {b.base_minor > b.member_minor && (
                      <span className={styles.basePrice}>
                        {formatPrice(b.base_minor)}
                      </span>
                    )}
                    <span
                      className={`${styles.memberPrice} ${isFree ? styles.freePrice : ""}`}
                    >
                      {isFree ? "FREE" : formatPrice(b.member_minor)}
                    </span>
                    {b.pct_off > 0 && !isFree && (
                      <span className={styles.discountChip}>-{b.pct_off}%</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <button
              className={styles.cta}
              onClick={handleSubscribe}
              disabled={purchasing}
            >
              {purchasing
                ? "Starting checkout…"
                : `Get membership · ${priceStr}`}
            </button>
            <p className={styles.disclaimer}>
              {arm === "autopay"
                ? `Auto-renews every ${per} at ${priceStr}. Cancel anytime.`
                : `One-time payment, no auto-charge. Renew yourself each ${per}.`}
            </p>
          </>
        )}
      </>
  );

  // Inline (Plan tab body) — no dimmed overlay / slide-up sheet / close button.
  if (inline) {
    return <div className={styles.inlineBody}>{body}</div>;
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div className={styles.handle} />
        <button className={styles.close} onClick={onClose} aria-label="Close">
          <IconX size={18} />
        </button>
        {body}
      </div>
    </div>
  );
}
